import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { DetailList } from "./DetailList";
import ReactHTMLTableToExcel from "react-html-table-to-excel";
import {
    resendInvoice,
    sendCancelSaleData,
} from "../../../apis/apis.dispatches/apisDispatch";

// ── shared cell classes ──────────────────────────────────────────────────────
const thBase = "px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide whitespace-nowrap border-b border-gray-200 dark:border-gray-600";
const tdBase = "px-3 py-2 text-xs whitespace-nowrap border-b border-gray-100 dark:border-gray-700 align-middle";

export function DistributionList({ orders, filename, questionState, onRefresh }) {
    const [items, setItems]               = useState([]);
    const [isProcessing, setIsProcessing] = useState(false);

    const operationValues = Object.values(orders);
    const isDetailed      = questionState.mode === "01";

    // ── action helpers ───────────────────────────────────────────────────────
    const handleCancelSale = async (item) => {
        if (!window.confirm("¿Está seguro de anular la venta?")) return;
        setIsProcessing(true);
        try {
            const res = await sendCancelSaleData({ operationID: item.operationID, userID: item.userID });
            toast.info(res.message);
            onRefresh?.();
        } catch { toast.error("Error al anular la venta"); }
        finally { setIsProcessing(false); }
    };

    const handleResendInvoice = async (item) => {
        setIsProcessing(true);
        try {
            const res = await resendInvoice({ operationID: item.operationID });
            toast.info(res.message);
            onRefresh?.();
        } catch { toast.error("Error al reenviar comprobante"); }
        finally { setIsProcessing(false); }
    };

    // ── action cell renderers ────────────────────────────────────────────────
    const CancelBtn = ({ item }) =>
        item.operationStatus != 200 ? (
            <button
                type="button"
                disabled={isProcessing}
                onClick={() => handleCancelSale(item)}
                className="inline-flex items-center gap-1 rounded-md bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white text-xs font-medium px-2.5 py-1 transition-colors"
                title="Anular venta"
            >
                {isProcessing
                    ? <span className="h-3 w-3 rounded-full border-2 border-white border-t-transparent animate-spin"></span>
                    : <i className="fas fa-ban"></i>}
                Anular
            </button>
        ) : (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 text-xs font-medium px-2.5 py-0.5">
                <i className="fas fa-check text-[10px]"></i>{item.operationStatus}
            </span>
        );

    const ResendBtn = ({ item }) =>
        item.httpStatusCode !== 200 && item.receiptSerial !== null ? (
            <div className="space-y-1">
                {item.httpStatusCode === 400 && (
                    <p className="text-xs text-red-600 dark:text-red-400 max-w-[160px] truncate" title={item.errorMessage}>
                        {item.errorMessage}
                    </p>
                )}
                <button
                    type="button"
                    disabled={isProcessing}
                    onClick={() => handleResendInvoice(item)}
                    className="inline-flex items-center gap-1 rounded-md bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-xs font-medium px-2.5 py-1 transition-colors"
                    title="Reenviar comprobante"
                >
                    {isProcessing
                        ? <span className="h-3 w-3 rounded-full border-2 border-white border-t-transparent animate-spin"></span>
                        : <i className="fas fa-paper-plane"></i>}
                    Reenviar
                </button>
            </div>
        ) : (
            <span className="text-xs text-gray-500 dark:text-gray-400">{item.httpStatusCode}</span>
        );

    // ── row builders ─────────────────────────────────────────────────────────
    const buildDetailedRows = (item) => {
        const payValues = Object.values(item.operationDetailDict);
        return payValues.map((itempay, i) => {
            const span = payValues.length;
            const first = i === 0;
            return (
                <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors">
                    {first && <td className={tdBase} rowSpan={span}>{item.operationType == "02" ? item.quotationRegisterDate : item.registerDate}</td>}
                    {first && <td className={tdBase} rowSpan={span}>{item.operationType == "02" ? item.quotationRegisterTime : item.registerTime}</td>}
                    {first && <td className={tdBase} rowSpan={span}>{item.operationType == "02" ? item.quotationUserName : item.userName}</td>}
                    {first && <td className={tdBase} rowSpan={span}>{item.operationType == "02" ? item.registerDate : ""}</td>}
                    {first && <td className={tdBase} rowSpan={span}>{item.operationType == "02" ? item.registerTime : ""}</td>}
                    {first && <td className={tdBase} rowSpan={span}>{item.operationType == "02" ? item.userName : ""}</td>}
                    {first && <td className={tdBase} rowSpan={span}>{item.clientObservation}</td>}
                    {first && <td className={`${tdBase} font-medium text-gray-800 dark:text-gray-200`} rowSpan={span}>{item.clientName}</td>}
                    {first && <td className={tdBase} rowSpan={span}>{item.clientPhysicalDistributionDisplay}</td>}
                    {first && <td className={tdBase} rowSpan={span}>{item.clientVisitDayDisplay}</td>}
                    {first && <td className={tdBase} rowSpan={span}>{item.clientAddress}</td>}

                    <td className={tdBase}>{itempay.productSku}</td>
                    <td className={`${tdBase} max-w-[180px] truncate`} title={itempay.productName}>{itempay.productName}</td>
                    <td className={`${tdBase} text-center`}>{itempay.unitName}</td>
                    <td className={`${tdBase} text-center font-medium`}>{itempay.quantity}</td>
                    <td className={`${tdBase} text-right`}>{Number(itempay.price).toFixed(2)}</td>
                    <td className={`${tdBase} text-right font-medium`}>{Number(itempay.subtotal).toFixed(2)}</td>

                    {first && <td className={tdBase} rowSpan={span}>{item.receiptSerial}</td>}
                    {first && <td className={tdBase} rowSpan={span}>{item.receiptNumber}</td>}
                    {first && <td className={`${tdBase} text-right font-semibold text-emerald-700 dark:text-emerald-400`} rowSpan={span}>S/ {Number(item.total).toFixed(2)}</td>}
                    {first && <td className={`${tdBase} text-right text-cyan-700 dark:text-cyan-400`} rowSpan={span}>S/ {Number(item.totalFree).toFixed(2)}</td>}
                    {first && <td className={`${tdBase} text-right text-amber-700 dark:text-amber-400`} rowSpan={span}>S/ {Number(item.quotationTotal).toFixed(2)}</td>}
                    {first && <td className={`${tdBase} text-center`} rowSpan={span}><CancelBtn item={item} /></td>}
                    {first && <td className={`${tdBase} text-center`} rowSpan={span}><ResendBtn item={item} /></td>}
                </tr>
            );
        });
    };

    const buildSummaryRow = (item, index) => (
        <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors">
            <td className={tdBase}>{item.operationType == "02" ? item.quotationRegisterDate : item.registerDate}</td>
            <td className={tdBase}>{item.operationType == "02" ? item.quotationRegisterTime : item.registerTime}</td>
            <td className={tdBase}>{item.operationType == "02" ? item.quotationUserName : item.userName}</td>
            <td className={tdBase}>{item.operationType == "02" ? item.registerDate : ""}</td>
            <td className={tdBase}>{item.operationType == "02" ? item.registerTime : ""}</td>
            <td className={tdBase}>{item.operationType == "02" ? item.userName : ""}</td>
            <td className={tdBase}>{item.clientObservation}</td>
            <td className={`${tdBase} font-medium text-gray-800 dark:text-gray-200`}>{item.clientName}</td>
            <td className={tdBase}>{item.clientPhysicalDistributionDisplay}</td>
            <td className={tdBase}>{item.clientVisitDayDisplay}</td>
            <td className={tdBase}>{item.clientAddress}</td>
            <td className={tdBase}>{item.receiptSerial}</td>
            <td className={tdBase}>{item.receiptNumber}</td>
            <td className={`${tdBase} text-right font-semibold text-emerald-700 dark:text-emerald-400`}>S/ {Number(item.total).toFixed(2)}</td>
            <td className={`${tdBase} text-right text-cyan-700 dark:text-cyan-400`}>S/ {Number(item.totalFree).toFixed(2)}</td>
            <td className={`${tdBase} text-right text-amber-700 dark:text-amber-400`}>S/ {Number(item.quotationTotal).toFixed(2)}</td>
            <td className={`${tdBase} text-center`}><CancelBtn item={item} /></td>
            <td className={`${tdBase} text-center`}><ResendBtn item={item} /></td>
        </tr>
    );

    const tbodies = isDetailed
        ? operationValues.map((item) => buildDetailedRows(item))
        : operationValues.map((item, i) => buildSummaryRow(item, i));

    // ── totals ───────────────────────────────────────────────────────────────
    const totalSales    = orders.reduce((s, o) => s + o.total, 0);
    const totalFree     = orders.reduce((s, o) => s + o.totalFree, 0);
    const totalQuotation = orders.reduce((s, o) => s + o.quotationTotal, 0);
    const infoColSpan   = isDetailed ? 19 : 13;

    if (!orders || orders.length === 0) {
        return (
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
                <div className="py-16 text-center">
                    <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-gray-100 dark:bg-gray-700 mb-4">
                        <i className="fas fa-truck text-gray-400 dark:text-gray-500 text-2xl"></i>
                    </div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Sin resultados</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                        Aplique los filtros y presione "Buscar distribuciones".
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">

            {/* Card header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                <div>
                    <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                        <i className="fas fa-list text-blue-500"></i>
                        Lista de Distribuciones
                    </h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {isDetailed ? "Vista detallada" : "Vista resumida"} · {orders.length} registros
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <ReactHTMLTableToExcel
                        id="test-table-xls-button"
                        className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium px-3 py-1.5 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        table="table-to-xls"
                        filename={filename}
                        sheet="report"
                        buttonText="↓ Excel"
                    />
                    <Link
                        to="/buys/new_purchase_order"
                        className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium px-3 py-1.5 transition-colors"
                    >
                        <i className="fas fa-plus"></i>
                        Nueva Orden
                    </Link>
                </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto max-h-[70vh] overflow-y-auto bg-white dark:bg-gray-900">
                <table className="min-w-full text-sm border-separate border-spacing-0" id="table-to-xls">
                    <thead className="sticky top-0 z-10">
                        {/* Group row */}
                        <tr>
                            <th colSpan={3} className={`${thBase} bg-blue-600 dark:bg-blue-800 text-white border-r border-blue-400`}>
                                <i className="fas fa-shopping-cart mr-1"></i>Preventa
                            </th>
                            <th colSpan={3} className={`${thBase} bg-cyan-600 dark:bg-cyan-800 text-white border-r border-cyan-400`}>
                                <i className="fas fa-truck mr-1"></i>Despacho
                            </th>
                            <th colSpan={isDetailed ? 18 : 12} className={`${thBase} bg-gray-600 dark:bg-gray-700 text-white`}>
                                <i className="fas fa-info-circle mr-1"></i>Información General
                            </th>
                        </tr>
                        {/* Column row */}
                        <tr>
                            {/* Preventa */}
                            {["Fecha","Hora","Usuario"].map(h => (
                                <th key={h} className={`${thBase} bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300`}>{h}</th>
                            ))}
                            {/* Despacho */}
                            {["Fecha","Hora","Usuario"].map(h => (
                                <th key={`d-${h}`} className={`${thBase} bg-cyan-50 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300`}>{h}</th>
                            ))}
                            {/* Info general */}
                            {["Código","Cliente","Sis. Dist.","Día","Dirección"].map(h => (
                                <th key={h} className={`${thBase} bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300`}>{h}</th>
                            ))}
                            {isDetailed && <>
                                <th className={`${thBase} bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300`}>SKU</th>
                                <th className={`${thBase} bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300`} style={{width:"18%"}}>Producto</th>
                                <th className={`${thBase} bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300`}>Unidad</th>
                                <th className={`${thBase} bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300`}>Cant.</th>
                                <th className={`${thBase} bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300`}>Precio</th>
                                <th className={`${thBase} bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300`}>Subtotal</th>
                            </>}
                            {["Serie","Correlativo"].map(h => (
                                <th key={h} className={`${thBase} bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300`}>{h}</th>
                            ))}
                            <th className={`${thBase} bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300`}>Total</th>
                            <th className={`${thBase} bg-cyan-100 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-300`}>Total Gratuito</th>
                            <th className={`${thBase} bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300`}>Total Preventa</th>
                            <th className={`${thBase} bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300`}>Anular</th>
                            <th className={`${thBase} bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300`}>Estado Envío</th>
                        </tr>
                    </thead>

                    <tbody>{tbodies}</tbody>

                    <tfoot>
                        <tr className="bg-gray-800 dark:bg-gray-950">
                            <td
                                colSpan={infoColSpan}
                                className="px-3 py-2 text-xs font-bold text-right text-white uppercase tracking-wide border-t border-gray-600"
                            >
                                <i className="fas fa-calculator mr-2"></i>TOTAL GENERAL
                            </td>
                            <td className="px-3 py-2 text-xs text-right font-bold text-emerald-300 whitespace-nowrap border-t border-gray-600">
                                S/ {Number(totalSales).toFixed(2)}
                            </td>
                            <td className="px-3 py-2 text-xs text-right font-bold text-cyan-300 whitespace-nowrap border-t border-gray-600">
                                S/ {Number(totalFree).toFixed(2)}
                            </td>
                            <td className="px-3 py-2 text-xs text-right font-bold text-amber-300 whitespace-nowrap border-t border-gray-600">
                                S/ {Number(totalQuotation).toFixed(2)}
                            </td>
                            <td colSpan={2} className="border-t border-gray-600 bg-gray-800 dark:bg-gray-950"></td>
                        </tr>
                    </tfoot>
                </table>
            </div>

            <DetailList items={items} />
        </div>
    );
}
